import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import Modal from '../Modal';

/**
 * Baskomponent för e-postmodalen som hanterar det gemensamma användargränssnittet
 * Denna komponent är avsedd att användas av mer specifika e-postkomponenter
 */
const EmailModal = ({ 
  isOpen,
  onClose,
  // Data för e-post
  recipient,
  recipientName,
  senderName,
  recipientType = 'Kund',
  subject: initialSubject = '',
  message: initialMessage = '',
  // Projektdata
  projectName = '',
  projectId,
  // Multi-recipient stöd
  allRecipients = [],
  // UI-konfiguration
  showTeamMembers: initialShowTeamMembers = false,
  // Låter avsändaren lägga till mottagare som inte finns i projektet — t.ex. en
  // kollega hos leverantören. Av som default: modalerna som bara ska nå kända
  // projektmedlemmar ska inte kunna skicka vidare till godtyckliga adresser.
  allowExtraRecipients = false,
  // Funktion som kallas när e-post skickas
  onEmailSent,
  // Konfiguration för att ändra projektets status (används för "Kom igång")
  changeProjectStatus = false,
  // Initialt e-postläge ('regular' eller 'welcome')
  initialEmailMode = 'regular'
}) => {

  // Lägg till denna funktion i början av komponenten för att säkerställa att alla recipient-objekt har consistent struktur
  const normalizeRecipients = (recipients) => {
    if (!recipients || !Array.isArray(recipients) || recipients.length === 0) return [];
    return recipients.map(r => ({
      email: r.email || '',
      name: r.name || '',
      role: r.role || 'Kund',
      type: r.type || 'customer',
      isMainRecipient: !!r.isMainRecipient,
      // Säkerställ att selected alltid finns och är en boolean
      selected: r.selected === undefined ? !!r.isMainRecipient : !!r.selected,
      isRequired: !!r.isRequired,
      // Manuellt tillagd mottagare. Måste bevaras här — funktionen bygger om
      // objekten vid varje toggle, så flaggan skulle annars tappas direkt.
      isExtra: !!r.isExtra
    }));
  };

  // Bilagor
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // Säkerställ att vi använder tomma strängar som standardvärden, aldrig undefined
  const [subject, setSubject] = useState(initialSubject || '');
  const [message, setMessage] = useState(initialMessage || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState(() => normalizeRecipients(allRecipients));
  const { user } = useAuth();
  const [showTeamMembers, setShowTeamMembers] = useState(initialShowTeamMembers);
  const [emailMode, setEmailMode] = useState(initialEmailMode);
  const [isInitialized, setIsInitialized] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  // Fälten för att lägga till en mottagare utanför projektet
  const [extraName, setExtraName] = useState('');
  const [extraEmail, setExtraEmail] = useState('');
  const [extraError, setExtraError] = useState('');
  const [hasChosenMain, setHasChosenMain] = useState(false);

  const selectedRecipientsRef = useRef([]);

  // Hantera filval
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments(prev => [...prev, {
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          contentBytes: base64,
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Rensa input så samma fil kan väljas igen
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Reset subject and message when modal is opened
  useEffect(() => {
    if (isOpen) {
      // Sätt emailMode till initialEmailMode när modalen öppnas
      setEmailMode(initialEmailMode);
      setAttachments([]);
      setManualEmail('');
      setHasChosenMain(false);
      
      if (initialEmailMode === 'welcome') {
        // Om vi öppnar i "Kom igång"-läge, sätt välkomstmeddelande och välj alla mottagare
        const updatedRecipients = normalizeRecipients(allRecipients).map(r => ({ ...r, selected: true }));
        setSelectedRecipients(updatedRecipients);
        setHasChosenMain(true);

        const mainRecipient = updatedRecipients.find(r => r.isMainRecipient);
        const firstName = mainRecipient?.name ? getFirstName(mainRecipient.name) : "alla";
        
        const welcomeMessage = `Hej ${firstName},

Nu är det dags att komma igång ordentligt med ditt projekt! Nu är alla delar på plats och det är dags för dig att börja fylla i val för produktval & rumsbeskrivning samt kika närmre på din tidplan. Ska bli riktigt kul att få genomföra detta projekt tillsammans!${generateSignature()}`;
        
        setSubject("Nu är det dags att komma igång!");
        setMessage(welcomeMessage);
        setShowTeamMembers(true);
      } else {
        // Vanlig e-post - säkerställ att initialSubject alltid är en sträng
        setSubject(initialSubject || '');
      }
      
      // Set focus to subject field when modal opens
      setTimeout(() => {
        const subjectInput = document.getElementById('subject');
        if (subjectInput) {
          subjectInput.focus();
        }
      }, 100);
    }
    // allRecipients och generateSignature utelämnade med avsikt. allRecipients
    // är en prop med defaultvärde [] — ett sådant default skapar en NY array
    // vid varje rendering när propen inte skickas in, så effekten hade
    // ominitierat modalen varje gång. generateSignature definieras längre ned
    // och får också ny identitet per rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSubject, initialEmailMode]);

  // Update ref when state changes
  useEffect(() => {
    selectedRecipientsRef.current = selectedRecipients;
  }, [selectedRecipients]);

  // Reset mode when modal is opened first time
  useEffect(() => {
    if (isOpen && !isInitialized) {
      setEmailMode('regular');
      setIsInitialized(true);
    }
    
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen, isInitialized]);

  // Extract first name from full name
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Find main recipient (person clicked on)
  const getMainRecipient = () => {
    return selectedRecipients.find(r => r.isMainRecipient) || null;
  };
  
  // Get all other recipients (everyone except main recipient)
  const getOtherRecipients = () => {
    return selectedRecipients.filter(r => !r.isMainRecipient);
  };

  // Initialize selected recipients based on props
  useEffect(() => {
    if (isOpen && !isInitialized) {
      // Filter out logged in user from recipient list
      const filteredRecipients = (allRecipients || []).filter(r => r.email !== user?.email);
      
      // If we have a specific recipient (from individual email button) and it's not in allRecipients
      if (recipient && !filteredRecipients.some(r => r.email === recipient)) {
        // Make sure recipient is not logged in user
        if (recipient !== user?.email) {
          setSelectedRecipients(normalizeRecipients([{
            email: recipient,
            name: recipientName || '',
            selected: true,
            role: recipientType,
            type: recipientType.toLowerCase(),
            isMainRecipient: true
          }]));
          setHasChosenMain(true);
        } else {
          setSelectedRecipients([]);
        }
      }
      // If we have multiple recipients (including main recipient from TeamMember)
      else if (filteredRecipients.length > 0) {
        // Find the main recipient
        const mainRecipient = filteredRecipients.find(r => r.isMainRecipient);

        // Sort recipients: main recipient marked, all others
        let initializedRecipients = [];

        // Add main recipient first if present
        if (mainRecipient) {
          initializedRecipients.push({
            ...mainRecipient,
            selected: true
          });
        }

        // Filter out main recipient from all other recipients
        const otherRecipients = filteredRecipients.filter(r => !r.isMainRecipient);

        // Add all other recipients (respect pre-selected, default to false)
        initializedRecipients = [
          ...initializedRecipients,
          ...otherRecipients.map(r => ({ ...r, selected: !!r.selected }))
        ];

        setSelectedRecipients(normalizeRecipients(initializedRecipients));
        // Om en huvudmottagare redan finns (bakåtkompatibilitet), sätt hasChosenMain direkt
        if (mainRecipient) {
          setHasChosenMain(true);
        }
      }
    }
  }, [isOpen, isInitialized, recipient, recipientName, recipientType, allRecipients, user]);

  // Hjälpfunktion för att generera signatur med kontaktuppgifter
  const generateSignature = () => {
    // Säkerställ exakt två radbrytningar före signaturtexten
    const signature = `\n\nMed vänliga hälsningar,\n${senderName || 'Ditt namn'}`;
    
    // Extrahera projekt-ID från projektnamnet (format: Projektnamn #12345)
    let projectIdFromName = '';
    if (projectName) {
      const match = projectName.match(/#\d+/);
      if (match) {
        projectIdFromName = match[0];
      }
    }
    
    // Lägg till projekt-ID direkt efter namn
    const projectInfo = `\nProjekt ID: ${projectIdFromName || '#XXXXX'}`;
    
    // Lägg till kontaktuppgifter sist om det är en kund som skickar
    let contactInfo = '';
    if (user && user.role === 'kund') {
      // Lägg till en tomrad mellan projektID och kontaktuppgifter
      contactInfo += '\n';
      
      // Lägg till telefon först - kontrollera flera möjliga egenskapsnamn
      const phoneNumber = user.phoneNumber || user.phone || user.tel || user.telephone || '';
      if (phoneNumber && phoneNumber.trim() !== '') {
        contactInfo += `\nTel: ${phoneNumber}`;
      } else {
        // Om vi inte har något telefonnummer, lägg till en platshållare
        contactInfo += `\nTel: ej tillgängligt`;
      }
      
      // Lägg till e-post sist
      contactInfo += `\nEpost: ${user.email}`;
    }
    
    return signature + projectInfo + contactInfo;
  };

  // Set initial greeting and signature when modal opens
  useEffect(() => {
    // Only run this once when modal opens, after selectedRecipients has been initialized, and main recipient is chosen
    if (isOpen && isInitialized && hasChosenMain && !success && selectedRecipients.length > 0 && message === '') {
      // Choose greeting based on whether we're sending to one or multiple recipients
      let greeting;

      // Get the main recipient if there is one
      const mainRecipient = selectedRecipients.find(r => r.isMainRecipient);
      const mainRecipientName = mainRecipient?.name || recipientName;

      if (mainRecipientName) {
        const recipientFirstName = getFirstName(mainRecipientName);
        greeting = `Hej ${recipientFirstName},\n\n`;
      } else {
        greeting = `Hej,\n\n`;
      }

      // Använd den nya signaturfunktionen
      const signature = generateSignature();

      // Use initialMessage if provided, otherwise create a default message
      // But always make sure signature is included
      if (initialMessage) {
        // Check if initialMessage already contains the signature
        if (!initialMessage.includes('Med vänliga hälsningar')) {
          // Add signature if it's not already there
          setMessage(initialMessage + signature);
        } else {
          setMessage(initialMessage);
        }
      } else {
        const defaultMessage = greeting + signature;
        setMessage(defaultMessage);
      }
    }

    // If there are required recipients, always show other members
    if (selectedRecipients.some(r => r.isRequired)) {
      setShowTeamMembers(true);
    }
    // generateSignature utelämnad med avsikt: den definieras i komponenten och
    // får ny identitet vid varje rendering. Effekten har redan message i listan
    // och anropar setMessage — en instabil lista här ger en loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitialized, hasChosenMain, success, selectedRecipients, recipientName, senderName, projectName, message, initialMessage]);

  // Handle tab from subject field to message field
  const handleSubjectKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const textarea = document.getElementById('message');
      const mainRecipient = selectedRecipients.find(r => r.isMainRecipient);
      let greeting;
      
      if (mainRecipient?.name) {
        const recipientFirstName = getFirstName(mainRecipient.name);
        greeting = `Hej ${recipientFirstName},\n\n`;
      } else if (recipientName) {
        const recipientFirstName = getFirstName(recipientName);
        greeting = `Hej ${recipientFirstName},\n\n`;
      } else {
        greeting = `Hej,\n\n`;
      }
      
      // Use setTimeout to let default tab focus happen first
      setTimeout(() => {
        if (textarea) {
          textarea.setSelectionRange(greeting.length, greeting.length);
        }
      }, 10);
    }
  };

  // Handle selection of specific recipients
  const handleRecipientToggle = (email) => {
    setSelectedRecipients(prev => 
      normalizeRecipients(prev.map(r => {
        // If recipient is required (consultant/architect), always selected
        if (r.isRequired) {
          return { ...r, selected: true };
        }
        // Otherwise, toggle only if it's the specific email
        return r.email === email 
          ? { ...r, selected: !r.selected } 
          : r;
      }))
    );
  };

  // Lägg till en mottagare som inte finns i projektet — leverantörens kollega,
  // en extra BK, vem det nu är som också behöver kopian.
  const handleAddExtraRecipient = () => {
    const email = extraEmail.trim();
    const name = extraName.trim();

    if (!email) {
      setExtraError('Ange en e-postadress');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setExtraError('E-postadressen ser inte giltig ut');
      return;
    }
    if (selectedRecipients.some(r => r.email.toLowerCase() === email.toLowerCase())) {
      setExtraError('Den mottagaren finns redan i listan');
      return;
    }

    setSelectedRecipients(prev => normalizeRecipients([
      ...prev,
      {
        email,
        name: name || email,
        role: 'Tillagd',
        type: 'extra',
        isMainRecipient: false,
        selected: true,
        isExtra: true
      }
    ]));

    setExtraName('');
    setExtraEmail('');
    setExtraError('');
  };

  // Ta bort en manuellt tillagd mottagare. Bara tillagda går att ta bort —
  // projektets egna mottagare kryssas ur i stället.
  const handleRemoveExtraRecipient = (email) => {
    setSelectedRecipients(prev =>
      normalizeRecipients(prev.filter(r => !(r.isExtra && r.email === email)))
    );
  };

  // Handle select/deselect all other recipients
  const handleSelectAllOthers = (selected) => {
    setSelectedRecipients(prev => 
      normalizeRecipients(prev.map(r => {
        // If recipient is required (consultant/architect), always selected
        if (r.isRequired) {
          return { ...r, selected: true };
        }
        // If not main recipient, follow selected parameter
        return !r.isMainRecipient
          ? { ...r, selected } 
          : r;
      }))
    );
  };

  // Handle selecting a main recipient from the list
  const handleSelectMainRecipient = (email) => {
    setSelectedRecipients(prev => normalizeRecipients(
      prev.map(r => ({
        ...r,
        isMainRecipient: r.email === email,
        selected: r.email === email ? true : r.selected
      }))
    ));
    setHasChosenMain(true);
    // Generera hälsning och signatur för vald person
    const chosen = selectedRecipients.find(r => r.email === email);
    if (chosen) {
      const firstName = getFirstName(chosen.name);
      const greeting = firstName ? `Hej ${firstName},\n\n` : `Hej,\n\n`;
      const signature = generateSignature();
      setMessage(greeting + signature);
    }
  };

  // Handle switching back to recipient selection
  const handleChangeMainRecipient = () => {
    setSelectedRecipients(prev => normalizeRecipients(
      prev.map(r => ({
        ...r,
        isMainRecipient: false,
        selected: false
      }))
    ));
    setHasChosenMain(false);
    setMessage('');
    setSubject('');
  };

  // Check if all other recipients are selected
  const areAllOtherRecipientsSelected = () => {
    const otherRecipients = getOtherRecipients();
    // En mottagare räknas som "vald" om antingen selected=true ELLER isRequired=true
    return otherRecipients.length > 0 && otherRecipients.every(r => r.selected || r.isRequired);
  };

  // Check if any recipient is selected
  const isAnyRecipientSelected = () => {
    // Inkludera manuellt inmatad e-post eller obligatoriska mottagare
    if (manualEmail.trim()) return true;
    return selectedRecipients.some(r => r.selected || r.isRequired);
  };

  // Check if there are any other recipients
  const hasOtherRecipients = () => {
    return getOtherRecipients().length > 0;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      setError('Vänligen fyll i både ämne och meddelande.');
      return;
    }

    // Check that at least one recipient is selected
    if (!isAnyRecipientSelected()) {
      setError('Du måste välja minst en mottagare.');
      return;
    }

    setSending(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Du är inte inloggad. Vänligen logga in igen.');
      }
      
      // Filter selected recipients - inkludera också alla som är markerade som obligatoriska
      const activeRecipients = selectedRecipients.filter(r => r.selected || r.isRequired);

      // Lägg till manuellt inmatad e-post om ingen huvudmottagare finns
      if (manualEmail.trim() && !mainRecipient) {
        activeRecipients.unshift({
          email: manualEmail.trim(),
          name: recipientName || manualEmail.trim(),
        });
      }
      
      // Prepare request data based on whether sending to one or multiple
      const requestData = {
        subject: subject,
        message: message,
        senderName: senderName,
        attachments: attachments.length > 0 ? attachments : undefined,
      };
      
      if (activeRecipients.length > 1) {
        requestData.recipients = activeRecipients.map(r => ({
          name: r.name,
          email: r.email
        }));
        requestData.recipientName = 'alla'; // Generic name for multiple recipients
      } else if (activeRecipients.length === 1) {
        requestData.to = activeRecipients[0].email;
        requestData.recipientName = activeRecipients[0].name;
      } else {
        throw new Error('Inga mottagare valda.');
      }
      
      // Send email first
      const response = await fetch(`${API_URL}/api/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Fel från server:', data);
        throw new Error(data.message || 'Kunde inte skicka e-post.');
      }

      // --- ONLY RUN THIS FOR "Kom igång" EMAILS ---
      if (emailMode === 'welcome' && user?.role === 'byggkonsult' && changeProjectStatus) {
        try {
          if (!projectId) throw new Error('projectId saknas vid statusuppdatering!');

          const updateStatusResponse = await fetch(`${API_URL}/api/projects/update-getting-started-status`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ projectId }),
          });

          const statusData = await updateStatusResponse.json();

          if (!updateStatusResponse.ok) {
            console.error('Kunde inte uppdatera projektets status:', statusData);
            throw new Error(statusData.message || 'Kunde inte uppdatera projektets status');
          } else {
            setSuccess(true);
            setSubject('');
            setMessage('');
            
            if (onEmailSent) {
              onEmailSent({ needsRefresh: true });
            }
            
            setTimeout(() => {
              onClose();
              setSuccess(false);
              window.location.reload();
            }, 1000);
            return;
          }
        } catch (statusError) {
          console.error('Fel vid uppdatering av projektets status:', statusError);
          alert(`E-post skickades, men statusuppdateringen misslyckades: ${statusError.message}. Försök igen eller kontakta support.`);
          setError(`E-post skickades, men statusuppdateringen misslyckades: ${statusError.message}`);
          setSending(false);
          return;
        }
      }

      // --- Regular emails (not "Kom igång") ---
      setSuccess(true);
      setSubject('');
      setMessage('');
      
      if (onEmailSent) {
        onEmailSent({ success: true, message });
      }
      
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Fel vid skickande av e-post:', error);
      setError(error.message || 'Ett fel uppstod. Försök igen senare.');
    } finally {
      setSending(false);
    }
  };

  const mainRecipient = getMainRecipient();
  const otherRecipients = getOtherRecipients();
  const selectedCount = selectedRecipients.filter(r => r.selected).length + (manualEmail.trim() && !mainRecipient ? 1 : 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div className="p-5">
        <h2 className="text-xl font-semibold text-leadme-gray-dark mb-4">Skicka e-post</h2>
        
        {/* Mode buttons borttagna - "Kom igång" har nu egen modal */}
        
        {success ? (
          <div className="bg-green-50 text-green-800 p-4 rounded-lg mb-4">
            <p>Ditt meddelande har skickats!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mottagare
              </label>
              
              {/* Recipients section */}
              {!hasChosenMain && selectedRecipients.length > 0 ? (
                /* Välj huvudmottagare-läge */
                <div className="rounded-md border border-gray-200 mb-2 overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">Välj huvudmottagare</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {selectedRecipients.map((r, i) => (
                      <div
                        key={i}
                        onClick={() => handleSelectMainRecipient(r.email)}
                        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-green-50 transition-colors"
                      >
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{r.name}</div>
                          <div className="text-xs text-gray-500">{r.role} &middot; {r.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Huvudmottagare vald — visa som förut */
                <div className="rounded-md border border-gray-200 mb-2 overflow-hidden">
                  {/* Manuell e-postinmatning om ingen huvudmottagare finns */}
                  {!mainRecipient && (
                    <div className="bg-gray-50 p-3 border-b border-gray-200">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Leverantörens e-post</label>
                      <input
                        type="email"
                        value={manualEmail}
                        onChange={(e) => setManualEmail(e.target.value)}
                        placeholder="Ange e-postadress..."
                        className="w-full text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                      />
                    </div>
                  )}
                  {/* Main recipient - always visible */}
                  {mainRecipient && (
                    <div className="bg-green-50 p-3 border-b border-gray-200">
                      <div className="flex items-center justify-between py-1">
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            id="main-recipient"
                            checked={mainRecipient.selected}
                            onChange={() => handleRecipientToggle(mainRecipient.email)}
                            className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                          />
                          <label htmlFor="main-recipient" className="ml-2 text-sm font-medium text-gray-800 flex-1 truncate">
                            {mainRecipient.name} ({mainRecipient.role}{mainRecipient.company ? `, ${mainRecipient.company}` : ''})
                          </label>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-xs text-gray-600 truncate">{mainRecipient.email}</span>
                          {selectedRecipients.length > 1 && (
                            <button
                              type="button"
                              onClick={handleChangeMainRecipient}
                              className="text-xs text-leadme-pink hover:text-leadme-pink-dark whitespace-nowrap"
                            >
                              Byt mottagare
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other project members - expandable section */}
                  {(hasOtherRecipients() || allowExtraRecipients) && (
                    <div className="bg-white">
                      <div
                        onClick={() => setShowTeamMembers(!showTeamMembers)}
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-700">
                            {allowExtraRecipients ? 'Övriga mottagare' : 'Övriga projektmedlemmar'}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            {otherRecipients.filter(t => t.selected || t.isRequired).length === 0
                              ? "Inga valda"
                              : `${otherRecipients.filter(t => t.selected || t.isRequired).length} valda`}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <svg
                            className={`w-4 h-4 text-gray-500 transform transition-transform ${showTeamMembers ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {/* Expanded content for other project members */}
                      {showTeamMembers && (
                        <div className="p-3 pt-0 border-t border-gray-100 bg-gray-50">
                          {hasOtherRecipients() && (
                            <div className="flex items-center justify-between mb-2 mt-2">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="select-all-others"
                                  checked={areAllOtherRecipientsSelected()}
                                  onChange={(e) => handleSelectAllOthers(e.target.checked)}
                                  className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
                                />
                                <label htmlFor="select-all-others" className="ml-2 text-sm font-medium text-gray-700">
                                  Välj alla övriga
                                </label>
                              </div>
                            </div>
                          )}

                          {/* List of other project members */}
                          <div className="space-y-1 ml-6">
                            {otherRecipients.map((member, index) => (
                              <div key={index} className="flex items-center justify-between py-1">
                                <div className="flex items-center flex-1">
                                  <input
                                    type="checkbox"
                                    id={`other-member-${index}`}
                                    checked={member.selected || member.isRequired}
                                    onChange={() => handleRecipientToggle(member.email)}
                                    disabled={member.isRequired}
                                    className={`rounded border-gray-300 ${
                                      member.isRequired
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'text-leadme-pink focus:ring-leadme-pink'
                                    }`}
                                  />
                                  <label
                                    htmlFor={`other-member-${index}`}
                                    className={`ml-2 text-sm ${
                                      member.isRequired
                                        ? 'text-gray-700 italic'
                                        : 'text-gray-700'
                                    } flex-1 truncate`}
                                  >
                                    <span>{member.name} ({member.role}{member.company ? `, ${member.company}` : ''})</span>
                                    {member.isRequired && (
                                      <span className="ml-1 text-xs text-leadme-pink">(Obligatorisk)</span>
                                    )}
                                  </label>
                                </div>
                                <span className="text-xs text-gray-500 truncate ml-2">{member.email}</span>
                                {member.isExtra && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExtraRecipient(member.email)}
                                    className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                    title={`Ta bort ${member.name}`}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Lägg till mottagare utanför projektet */}
                          {allowExtraRecipients && (
                            <div className={`${hasOtherRecipients() ? 'mt-3 pt-3 border-t border-gray-200' : 'mt-2'}`}>
                              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                                Lägg till mottagare
                              </label>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                  type="text"
                                  value={extraName}
                                  onChange={(e) => setExtraName(e.target.value)}
                                  placeholder="Namn (valfritt)"
                                  className="sm:w-1/3 text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                />
                                <input
                                  type="email"
                                  value={extraEmail}
                                  onChange={(e) => { setExtraEmail(e.target.value); setExtraError(''); }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddExtraRecipient();
                                    }
                                  }}
                                  placeholder="E-postadress"
                                  className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                />
                                <button
                                  type="button"
                                  onClick={handleAddExtraRecipient}
                                  className="text-sm px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                                >
                                  Lägg till
                                </button>
                              </div>
                              {extraError && (
                                <p className="mt-1 text-xs text-red-500">{extraError}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Status for selected recipients */}
              {hasChosenMain && (
                <div className="flex justify-between items-center text-xs text-gray-500 px-1 mb-2">
                  <span>
                    {selectedCount === 0
                      ? "Ingen mottagare vald"
                      : selectedCount === 1
                        ? "1 mottagare vald"
                        : `${selectedCount} mottagare valda`}
                  </span>
                </div>
              )}
            </div>
            
            {hasChosenMain && (
              <>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Ämne
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    onKeyDown={handleSubjectKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                    placeholder="Ange ämne"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Meddelande
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent h-48"
                    placeholder="Skriv ditt meddelande här..."
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 italic">
                    Hälsningsfras och underskrift är redan tillagda. Skriv ditt meddelande mellan dem.
                  </p>
                </div>

                {/* Bilagor */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bilagor
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-leadme-pink border border-gray-300 rounded-md px-3 py-1.5 hover:border-leadme-pink transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Bifoga fil
                  </button>
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate text-gray-700">{file.name}</span>
                            <span className="text-gray-400 flex-shrink-0">({formatFileSize(file.size)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="bg-leadme-red-light/20 text-leadme-red p-3 rounded-md mb-4">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={sending || !isAnyRecipientSelected()}
                    className="flex-1 px-2.5 py-1.5 sm:px-3 text-sm font-medium bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:bg-leadme-pink/50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {sending ? 'Skickar...' : 'Skicka'}
                  </button>
                </div>
              </>
            )}

            {!hasChosenMain && (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </Modal>
  );
};

export default EmailModal; 